import { useState, useEffect } from "react";
import { Modal, Button } from "@mantine/core";
import { useIdleTimeContext } from "./IdleTimeContext";
import {
  useFetchTask,
  useNextPendingTask,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { IconAlertCircleFilled } from "@tabler/icons-react";

interface IdleWarningModalProps {
  jobId: string;
  taskId: string;
}

export const IdleWarningModal = ({ jobId, taskId }: IdleWarningModalProps) => {
  const [showModal, setShowModal] = useState(false);
  const { data: task, refetch: refetchTask } = useFetchTask(jobId, taskId);
  const { refetch: refetchNextPendingTask } = useNextPendingTask(jobId);

  const { isIdle, reset, setTaskExpired } = useIdleTimeContext();

  // Show modal when user becomes idle
  useEffect(() => {
    if (isIdle) {
      setShowModal(true);
    }
  }, [isIdle]);

  // Dev-only: listen for reset event to close modal
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    const handler = () => setShowModal(false);
    window.addEventListener("dev:reset-modals", handler);
    return () => window.removeEventListener("dev:reset-modals", handler);
  }, []);

  const handleContinue = async () => {
    // fetch the task
    const result = await refetchTask();
    if (result.error) {
      console.error(result.error);
    }
    if (result.data) {
      // If it has been picked up by someone else,
      // show the ExitModal by setting taskExpired to true
      if (
        result.data.assigned_to &&
        result.data.assigned_to !== task?.assigned_to
      ) {
        setShowModal(false);
        setTaskExpired(true);
        return;

        // If it has been unassigned, fetch the next pending task
        // if it is the same task, close this modal and continue working
      } else if (!result.data.assigned_to) {
        const nextPendingTask = await refetchNextPendingTask();
        if (nextPendingTask?.data?.id !== taskId) {
          setShowModal(false);
          setTaskExpired(true);
          return;
        }
      }
    }
    setShowModal(false);
    reset();
  };

  return (
    <Modal
      opened={showModal}
      onClose={handleContinue}
      title="Are you still there?"
      centered
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      overlayProps={{
        backgroundOpacity: 0.6,
        blur: 3,
      }}
      zIndex={10100}
      lockScroll={false}
      withinPortal
      portalProps={{ target: "body" }}
      styles={{
        overlay: { position: "fixed", inset: 0 },
        inner: { position: "fixed", inset: 0 },
      }}
    >
      <div className="flex flex-col items-center gap-6 py-4">
        <div className="flex h-16 w-16 items-center justify-center">
          <IconAlertCircleFilled size={50} color="orange" />
        </div>

        <div className="text-center">
          <p className="text-gray-600">
            We haven't detected any activity for a while.
          </p>
        </div>

        <Button onClick={handleContinue} variant="outline" size="md" fullWidth>
          Continue Working
        </Button>
      </div>
    </Modal>
  );
};
